// @flow
import React from "react";
import moment from "moment";
import { rgba } from "polished";
import styled, { css } from "styled-components";

import { Link } from "../common/Link";
import Sticker from "./Sticker";
import Gettext from "../common/Gettext";
import tournamentBgIron from "./images/default-tournament-bg-iron.jpg";
import tournamentBgSiam from "./images/default-tournament-bg-th.jpg";
import RawTimer, { getDuration } from "./Timer";
import PrizeFund from "./PrizeFund";
import { Cup, Person } from "../Icons/Tournaments";
import ButtonSwitcher from "./Account/Card/ButtonSwitcher";

import { ModalConsumer, type ShowModal } from "../common/ModalProvider";
import { JoinPopup, LeavePopup } from "./Popups";
import { fontSize, rtl } from "../common/styled-mixins";

const Tournament = styled.div`
  direction: ${({ theme }) => theme.direction};
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  max-width: 463px;
  min-height: 144px;
  margin-bottom: 24px;
  padding: 3px;
  font-size: ${fontSize(12)};
  color: #fff;
  background-color: ${({ theme }) => theme.main};
  background-image: linear-gradient(
      to bottom,
      ${({ theme }) => rgba(theme.main, 0.75)},
      ${({ theme }) => rgba(theme.main, 0.75)}
    ),
    url(${({ bg }) =>
      bg
        ? bg
        : process.env.REACT_APP_DEPLOY_TARGET === "siamoption"
        ? tournamentBgSiam
        : tournamentBgIron});
  background-size: cover;
  background-repeat: no-repeat;
  background-position: 50%;

  ${(props) =>
    props.active &&
    css`
      box-shadow: inset 0 0 0 3px ${props.theme.accent};
    `};

  @media (max-width: 959px) {
    align-self: center;
  }

  @media (max-width: 479px) {
    width: 100%;
    flex-direction: column;
    padding: 18px 3px;
    background-size: cover;
  }
`;

const Title = styled(Link)`
  display: inline-flex;
  ${rtl("margin-bottom", "12px", "8px")};
  font-size: ${fontSize(16)};
  font-weight: 700;
  color: ${({ theme }) => theme.foreground};
  text-decoration: none;

  ${({ theme }) =>
    theme.locale === "ar" &&
    css`
      padding-left: 15px;
    `};
`;

const FirstHalf = styled.div`
  direction: ${({ theme }) => theme.direction};
  min-width: 55%;
  flex-basis: 55%;
  ${rtl("padding-left", "20px")};
  color: ${({ theme }) => theme.foreground};

  @media (max-width: 479px) {
    flex-basis: 100%;
    min-width: 100%;
  }
`;

const LastHalf = styled.div`
  min-width: 45%;
  flex-basis: 45%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  ${(props) =>
    !props.isCompleted &&
    css`
      margin-top: 34px;
    `};

  @media (max-width: 479px) {
    display: block;
    padding-top: 10px;
    text-align: center;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  align-content: center;
  align-items: center;
  width: 163px;
  height: 33px;
  font-weight: 700;
  font-size: ${fontSize(16)};
  color: ${({ theme }) => theme.foreground};
  text-align: center;
`;

const MoreLink = styled(Link)`
  margin-top: 20px;
  font-size: ${fontSize(12)};
  color: ${(props) => props.theme.accent};
  text-decoration: underline;

  :hover {
    text-decoration: none;
  }

  @media (max-width: 479px) {
    display: inline-block;
  }
`;

const Place = styled.div`
  font-size: ${fontSize(10)};
  color: #99959d;
`;

const UserPlace = styled.span`
  font-size: ${fontSize(14)};
  font-weight: 700;
  color: ${(props) => props.theme.accent};
`;

const AllPlaces = styled.span`
  font-size: ${fontSize(14)};
  font-weight: 700;
  color: ${({ theme }) => theme.foreground};
`;

const UnavailableText = styled.span`
  font-weight: 700;
  font-size: ${fontSize(16)};
  color: ${({ theme }) => theme.foreground};
  text-align: center;
`;

const EndText = styled(UnavailableText)`
  margin-top: 15px;

  @media (max-width: 479px) {
    display: block;
  }
`;

const CupContainer = styled.div`
  margin-top: 7px;
  color: ${({ theme }) => theme.foreground};

  ${({ place, theme }) => {
    if (place === 1) {
      return css`
        color: ${theme.accent};
      `;
    }

    if (place === 2) {
      return css`
        color: #bebebe;
      `;
    }

    if (place === 3) {
      return css`
        color: #df9784;
      `;
    }
  }};
`;

const Timer = styled(RawTimer)`
  margin-bottom: 12px;
`;

const Participants = styled.div`
  font-weight: 700;
  font-size: ${fontSize(14)};
  color: ${({ theme }) => theme.foreground};
`;

const PersonIcon = styled(Person)`
  ${rtl("margin-right", "8px")};
`;

const TournamentDate = styled.div`
  margin-bottom: 12px;
  font-weight: 400;
  font-size: ${fontSize(12)};
  line-height: 1.4;
  color: inherit;
  opacity: 0.5;
`;

const TournamentCard = (props) => {
  const isParticipating = props.participation_status === 1;
  const timeInHours = Math.floor(
    (props.start_date * 1000 - props.time) / 1000 / 3600
  );

  const statusIsStartSoon =
    props.type === "free" && props.subtype === false && timeInHours >= 12;

  return (
    <Tournament
      active={props.active}
      bg={props.images && props.images.bar != null ? props.images.bar : null}
    >
      <FirstHalf>
        <Title to={`/tournaments/${props.id}`}>{props.title}</Title>
        {!props.isCompleted ? (
          <Timer
            completedTimer={moment(props.finish_date * 1000).format("LLL")}
            days={getDuration(props, "days")}
            hours={getDuration(props, "hours")}
            minutes={getDuration(props, "minutes")}
            seconds={getDuration(props, "seconds")}
            active={props.active}
            isCompleted={props.isCompleted}
            start_date={props.start_date}
            finish_date={props.finish_date}
            publish_time={props.publish_time}
            toStart
          />
        ) : (
          <TournamentDate>
            <div>
              <Gettext>The tournament is finished</Gettext>
            </div>
            {moment(props.start_date * 1000).format("LL")} /{" "}
            {moment(props.finish_date * 1000).format("LL")}
          </TournamentDate>
        )}
        <PrizeFund
          active={props.active}
          isCompleted={props.isCompleted}
          prize_fund_usd={props.prize_fund_usd}
          prize_fund={props.prize_fund}
        />
      </FirstHalf>
      <LastHalf isCompleted={props.isCompleted}>
        {props.isCompleted ? (
          <React.Fragment>
            {isParticipating ? (
              <React.Fragment>
                {props.userPlace > 0 && props.userPlace < 4 && (
                  <CupContainer place={props.userPlace}>
                    <Cup width="16px" height="21px" />
                  </CupContainer>
                )}
                <Place>
                  <UserPlace># {props.userPlace}</UserPlace>{" "}
                  <Gettext>of</Gettext>{" "}
                  <AllPlaces>{props.participantsCount}</AllPlaces>
                </Place>
              </React.Fragment>
            ) : (
              <Participants>
                <PersonIcon />
                {props.participant_count}
              </Participants>
            )}
            <EndText>
              <Gettext>The tournament is finished</Gettext>
            </EndText>
            <MoreLink to={`/tournaments/${props.id}`}>
              <Gettext>Learn more</Gettext>
            </MoreLink>
          </React.Fragment>
        ) : (
          <React.Fragment>
            {statusIsStartSoon ? (
              <UnavailableText>
                <Gettext>Starts soon</Gettext>
              </UnavailableText>
            ) : (props.isLimited && props.isLimitedForMoney) ||
              props.invited ||
              props.subtype === "demo" ? (
              <ButtonContainer>
                <ModalConsumer>
                  {({ showModal }: { showModal: ShowModal }) => (
                    <ButtonSwitcher
                      flat={true}
                      active={props.active}
                      isCompleted={props.isCompleted}
                      isParticipating={isParticipating}
                      {...props}
                      onActiveTradeClick={() =>
                        props.onActivateTournament(props.id)
                      }
                      onTradeClick={() => props.onActivateTournament(props.id)}
                      onJoinClick={() =>
                        showModal(JoinPopup, {
                          ...props,
                          dontRedirect: true,
                          fromTournamentsList: true,
                        })
                      }
                      onLeaveClick={() =>
                        showModal(LeavePopup, {
                          ...props,
                          dontRedirect: true,
                          fromTournamentsList: true,
                        })
                      }
                    />
                  )}
                </ModalConsumer>
              </ButtonContainer>
            ) : (
              <UnavailableText>
                <Gettext>Available by invitation only</Gettext>
              </UnavailableText>
            )}
            <MoreLink to={`/tournaments/${props.id}`}>
              <Gettext>Learn more</Gettext>
            </MoreLink>
          </React.Fragment>
        )}
      </LastHalf>
      <Sticker
        type={props.type}
        subtype={props.subtype}
        isNew={props.isNew}
        active={props.active}
        limited={props.isLimited}
      />
    </Tournament>
  );
};

export default TournamentCard;



// WEBPACK FOOTER //
// ./src/components/Tournaments/Tournament.js